import type { LoginFlow, RegistrationFlow } from '@ory/client';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { z } from 'zod';
import { useAuthStore } from '@/lib/auth';
import { getAccessiblePath } from '@/lib/auth/lib/permission-and-role/get-accessible-path';
import { authService, logoutService, signInService } from '@/lib/auth/ory/service';
import { getErrorResponseData, getSsoLinkedEmail, getSsoLinkedProvider, hasHttpStatus, is400Error, isFlowExpired, isSsoCredentialsLinkedMessage, type OryKratosErrorResponse } from '@/lib/auth/ory/service/types';
import { clearAllQueries } from '@/lib/query';
import { queryClient } from '@/lib/queryClient';
import { shouldRequireAdditionalAuth } from './-utils/shouldRequireAdditionalAuth';
type SignInLoaderData = {
  flow: LoginFlow | RegistrationFlow;
  needsAdditionalAuth: boolean;
  ssoAutoRetry: boolean;
  ssoLinkedEmail?: string;
  ssoLinkedProvider?: string;
  flowExpired?: boolean;
};
const SearchSchema = z.object({
  redirect: z.string().optional(),
  flow: z.string().optional()
});

/**
 * セッションを破棄する（Kratos のセッション無効化 + Query キャッシュ全破棄）
 *
 * ログイン中に MFA が有効化された場合や `session_already_available` 時など、
 * セッションをクリアして新規ログインフローを開始するために使用する。
 *
 * @see dev-query-cache スキル「セッション切替時のキャッシュクリア」
 */
async function invalidateSession(): Promise<void> {
  try {
    const logoutFlow = await logoutService.initFlow();
    if (logoutFlow.logout_token) {
      await logoutService.submitFlow(logoutFlow.logout_token);
    }
  } catch (error) {
    // ログアウトに失敗しても、ログインフローは続行する
    console.warn('Failed to invalidate session:', error);
  }
  try {
    await clearAllQueries(queryClient);
  } catch {
    // cancelQueries 失敗時も finally で clear は済む。ログインフローは続行する。
  }
}
export const Route = createFileRoute('/auth/sign-in/')({
  validateSearch: zodValidator(SearchSchema),
  // 認証済みで追加認証が不要な場合は、適切な管理画面TOPにリダイレクト
  beforeLoad: async ({
    search
  }) => {
    const store = useAuthStore.getState();
    const isAuthenticated = await store.isAuthenticated();
    if (isAuthenticated) {
      // セッションを取得して適切なリダイレクト先を決定
      const session = await store.getSession();
      const isIndividualOnly = session.hasIndividual && !session.hasOperator && !session.hasServiceAccount;
      const targetRedirectTo = (() => {
        if (isIndividualOnly) {
          const validIndividualRedirect = search.redirect && (search.redirect.startsWith('individual') || search.redirect.startsWith('/individual'));
          return validIndividualRedirect ? search.redirect : '/individual';
        }
        if (search.redirect) return search.redirect;
        const accessiblePath = getAccessiblePath(session.hasPermissionScopes) ?? '/';
        return session.hasOperator ? accessiblePath : '/individual';
      })();
      throw redirect({
        to: targetRedirectTo
      });
    }
  },
  loaderDeps: ({
    search
  }) => ({
    flowId: search.flow
  }),
  // サインインフローを初期化（または既存フローを取得）
  loader: async ({
    deps
  }): Promise<SignInLoaderData> => {
    // 全パス共通: セッションを確認し、MFAが必要なら優先的にAAL2フローを開始
    // beforeLoad で authenticated（MFA無効 or AAL2済み）は既にリダイレクト済みのため、
    // ここに到達するのは「未認証」か「MFA有効かつAAL1のみ」のケース
    const sessionResult = await authService.getSession('aal1');
    if (shouldRequireAdditionalAuth(sessionResult.data)) {
      const flow = await signInService.initFlow({
        aal: 'aal2'
      });
      return {
        flow,
        needsAdditionalAuth: true,
        ssoAutoRetry: false
      };
    }

    // Kratos からフローIDが渡された場合（SSO webhook 失敗時など）は既存フローを取得
    if (deps.flowId) {
      try {
        const flow = await signInService.getFlow(deps.flowId);
        return {
          flow,
          needsAdditionalAuth: false,
          ssoAutoRetry: false
        };
      } catch (loginFlowError) {
        // self_service_flow_expired (410): フロー失効。新規フローを発行して flowExpired: true でバナー表示する。
        if (isFlowExpired(loginFlowError)) {
          try {
            const flow = await signInService.initFlow();
            return {
              flow,
              needsAdditionalAuth: false,
              ssoAutoRetry: false,
              flowExpired: true
            };
          } catch (reinitError) {
            console.warn('Failed to re-init flow after expiration, falling through:', reinitError);
          }
        }

        // OIDC ログインで Webhook がフローを中断した場合、Kratos は Registration フローを作成する。
        // Login フローとして取得できない（404）場合は Registration フローを試す。
        if (hasHttpStatus(loginFlowError, 404)) {
          try {
            const registrationFlow = await signInService.getRegistrationFlow(deps.flowId);
            const ssoLinkedMessage = registrationFlow.ui.messages?.find(isSsoCredentialsLinkedMessage);
            return {
              flow: registrationFlow,
              needsAdditionalAuth: false,
              ssoAutoRetry: !!ssoLinkedMessage,
              ssoLinkedEmail: ssoLinkedMessage ? getSsoLinkedEmail(ssoLinkedMessage) : undefined,
              ssoLinkedProvider: ssoLinkedMessage ? getSsoLinkedProvider(ssoLinkedMessage) : undefined
            };
          } catch (registrationFlowError) {
            console.warn('Failed to get registration flow, falling back to new flow:', registrationFlowError);
          }
        } else {
          console.warn('Failed to get existing flow, falling back to new flow:', loginFlowError);
        }
      }
    }
    try {
      // 通常のログインフロー初期化
      const flow = await signInService.initFlow();
      return {
        flow,
        needsAdditionalAuth: false,
        ssoAutoRetry: false
      };
    } catch (error) {
      console.error('Failed to initialize sign in flow:', error);

      // session_already_available エラーの場合（セッションが存在するのにaal1フローを作成しようとした）
      if (is400Error(error)) {
        const errorData = getErrorResponseData<OryKratosErrorResponse>(error);
        if (errorData?.error?.id === 'session_already_available') {
          // セッションを破棄してから再度フローを初期化
          await invalidateSession();
          try {
            const flow = await signInService.initFlow();
            return {
              flow,
              needsAdditionalAuth: false,
              ssoAutoRetry: false
            };
          } catch (retryError) {
            console.error('Failed to initialize flow after session invalidation:', retryError);
            // それでも失敗した場合はエラーとして扱う
            throw retryError;
          }
        }
      }

      // それ以外のエラーはTanStack Routerのエラーバウンダリで処理
      throw error;
    }
  }
});